import React, { memo, useCallback, useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import ControlledCurrencyInput from 'transfix-form/components/ControlledCurrencyInput'
import { FormIf } from 'transfix-form/components/FormIf'
import InfoIcon from 'transfix-icons/build/InfoIcon'
import { Tooltip } from 'transfix-ui/components/Tooltip'
import Box from 'transfix-ui/core/Box'
import Grid from 'transfix-ui/core/Grid'
import Typography from 'transfix-ui/core/Typography'
import { TEST_IDS } from '../../constants'
import { useSavedLaneFormContext } from '../../context/SavedLaneFormContext'
import { getErrorMessage, TSavedLaneSchema } from '../../schema'
import { IRateTypes } from '../../types'
import ControlledSelectRateType from './ControlledSelectRateType'
import { getRateFieldPlaceholder } from './utils'

export interface IRateFormSectionProps {}

const RateFormSection = memo<IRateFormSectionProps>(() => {
  /**
   * Form Context
   */
  const { control, errors } = useSavedLaneFormContext()
  const rateType = useWatch<TSavedLaneSchema['rateType']>({ control, name: 'rateType' })
  const rateErrors = getErrorMessage(errors, 'rate')

  const rateInputPlaceholder = useMemo(() => {
    if (rateType) {
      return getRateFieldPlaceholder(rateType)
    }
    return ''
  }, [rateType])

  const showRateInput = useCallback((value: IRateTypes) => {
    return value !== IRateTypes.anyRate
  }, [])

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Box mb={1.5}>
          <Typography variant='subtitle1'>Minimum Rate</Typography>
        </Box>
        <Box display='flex' alignItems='center'>
          <Box mr={0.5}>
            <Typography variant='body2'>What rate do you want to receive?</Typography>
          </Box>
          <Tooltip trigger={<InfoIcon size='large' color='coolGray5' />}>
            We will send you loads as close to your set minimum rate as we can.
          </Tooltip>
        </Box>
      </Grid>
      <Grid item xs={5} md={3}>
        <ControlledSelectRateType control={control} name='rateType' />
      </Grid>
      <Grid item xs={7} md={6}>
        <FormIf control={control} name='rateType' when={showRateInput}>
          <ControlledCurrencyInput
            control={control}
            name='rate'
            CurrencyInputProps={{
              dataTest: TEST_IDS.rateInput,
              large: true,
              placeholder: rateInputPlaceholder,
              invalid: !!rateErrors,
              validationMessage: rateErrors,
            }}
          />
        </FormIf>
      </Grid>
    </Grid>
  )
})

export default RateFormSection
